import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import { UnfoldLess, UnfoldMore } from "@material-ui/icons";
import moment from "moment";
import React from "react";
import { Event as RbcEvent, ToolbarProps, View } from "react-big-calendar";

type Density = "tight" | "loose";

type Props = ToolbarProps<RbcEvent> & {
  density: Density;
  onToggleDensity: (d: Density) => void;
  currentView: View;
};

const DATE_INPUT_FORMAT = "YYYY-MM-DD";
const MONTH_INPUT_FORMAT = "YYYY-MM";

const CalendarToolbar: React.FC<Props> = ({
  date,
  label,
  localizer,
  view,
  views,
  onNavigate,
  onView,
  density,
  onToggleDensity,
  currentView,
}) => {
  const messages = localizer.messages;
  const viewNames = views as View[];
  const densityDisabled = currentView === "month" || currentView === "agenda";
  const isMonthJump = currentView === "month";
  const jumpInputType = isMonthJump ? "month" : "date";
  const jumpFormat = isMonthJump ? MONTH_INPUT_FORMAT : DATE_INPUT_FORMAT;
  const jumpLabel = isMonthJump ? "Go to month" : "Go to date";
  const jumpValue = moment(date).format(jumpFormat);

  const handleJumpChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!event.target.value) {
      return;
    }
    const picked = moment(event.target.value, jumpFormat);
    if (!picked.isValid()) {
      return;
    }
    // Month picker yields YYYY-MM; land on the 1st so the month view stays stable.
    const target = isMonthJump ? picked.startOf("month") : picked;
    onNavigate("DATE", target.toDate());
  };

  return (
    <div className="rbc-toolbar">
      <span className="rbc-btn-group">
        <button type="button" onClick={() => onNavigate("TODAY")}>
          {messages.today}
        </button>
        <button type="button" onClick={() => onNavigate("PREV")}>
          {messages.previous}
        </button>
        <button type="button" onClick={() => onNavigate("NEXT")}>
          {messages.next}
        </button>
      </span>

      <span className="rbc-toolbar-label booking-calendar-toolbar-label">
        <span>{label}</span>
        <TextField
          id="booking-calendar-jump-date"
          key={jumpInputType}
          label={jumpLabel}
          type={jumpInputType}
          size="small"
          variant="outlined"
          value={jumpValue}
          onChange={handleJumpChange}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "aria-label": jumpLabel }}
          className="booking-calendar-jump-date"
        />
      </span>

      <span className="rbc-btn-group" aria-label="Row density">
        {densityDisabled ? (
          <button
            type="button"
            aria-label="Row density (unavailable in this view)"
            disabled
            className="booking-calendar-density-toggle booking-calendar-density-toggle--disabled"
            style={{
              paddingTop: 7,
              paddingBottom: 3,
              opacity: 0.4,
              cursor: "not-allowed",
            }}
          >
            <UnfoldLess style={{ fontSize: 14 }} />
          </button>
        ) : (
          <Tooltip
            title={
              density === "tight"
                ? "Switch to loose rows"
                : "Switch to tight rows"
            }
            arrow
          >
            <button
              type="button"
              aria-label="Toggle row density"
              aria-pressed={density === "loose"}
              className={density === "loose" ? "rbc-active" : ""}
              onClick={() =>
                onToggleDensity(density === "tight" ? "loose" : "tight")
              }
              style={{ paddingTop: 7, paddingBottom: 3 }}
            >
              {density === "tight" ? (
                <UnfoldLess style={{ fontSize: 14 }} />
              ) : (
                <UnfoldMore style={{ fontSize: 14 }} />
              )}
            </button>
          </Tooltip>
        )}
      </span>

      <span className="rbc-btn-group">
        {viewNames.map((name) => (
          <button
            type="button"
            key={name}
            className={view === name ? "rbc-active" : ""}
            onClick={() => onView(name)}
          >
            {messages[name]}
          </button>
        ))}
      </span>
    </div>
  );
};

export default CalendarToolbar;
